import { PositionController } from './PositionController';
import SceneManager, { SLIDE_TRANSITION_DURATION } from '../logic/SceneManager';
import { View } from '@play-co/timestep-core/ui';
import { fromEntityId, PetEntity } from '../logic/EntityManager';
import { ViewPosition } from '@play-co/timestep-core/lib/ui/View';
import StateObserver from '../../StateObserver';
import { petActions } from '../pet/pet.redux';
import { createWaiterInView, getRootView, getScreenTop } from '../../lib/utils';
import { isPetSpeaking } from '../conversation/conversation.redux';
import { LivingRoomPosition, Position } from './PositionModel';
import { getPetAnimation } from '../appearance/appearance.config';

const wanderLocations: LivingRoomPosition['location'][] =
  // prettier-ignore
  [
    'tl', 'tc', 'tr',
    'lm', 'c', 'rm',
    'bl', 'bc', 'br',
  ];

export class PetPositionController extends PositionController<PetEntity> {
  private wanderWaiter = createWaiterInView(this.view);

  constructor(sceneManager: SceneManager, pet: PetEntity) {
    super(sceneManager, pet);
  }

  public setWandering(isWandering: boolean) {
    if (!isWandering) {
      this.wanderWaiter.clear();
      return;
    }

    void this.wander();
  }

  private async wander() {
    this.wanderWaiter.wait(() => this.wander(), 3000);

    const { care } = StateObserver.getState();
    if (!this.position) {
      return;
    }

    if (
      care.currentCareAction ||
      isPetSpeaking(fromEntityId(this.view.entityId).id)
    ) {
      return;
    }

    const { location, scene } = this.position;
    if (scene === 'livingRoom' || scene === 'friendsLivingRoom') {
      let newLocation: Position['location'];
      do {
        const randomLocationIndex = Math.floor(
          Math.random() * wanderLocations.length,
        );
        newLocation = wanderLocations[randomLocationIndex];
      } while (location === newLocation);

      await this.moveTo({
        scene,
        location: newLocation,
      });
    }
  }

  protected getTransitionDuration(
    scene: View,
    destination: Partial<ViewPosition>,
  ) {
    const superview = this.view.getSuperview();
    const { ui } = StateObserver.getState();
    if (scene === superview) {
      if (ui.isPetCrateOpening) {
        return 0;
      }
    } else if (superview !== null) {
      return SLIDE_TRANSITION_DURATION;
    }

    return super.getTransitionDuration(scene, destination);
  }

  protected prepareForTransition(sceneView: View) {
    const superview = this.view.getSuperview();
    if (superview === null) {
      super.prepareForTransition(sceneView);
      return;
    }

    if (sceneView === superview) return;

    this.view.updateOpts({ superview: getRootView() });
    this.view.setFlipped(false);
  }

  protected async animateViewPosition(
    sceneView: View,
    destination: Partial<ViewPosition>,
    duration: number,
  ) {
    if (sceneView !== this.view.getSuperview()) {
      destination.y += getScreenTop();
    }
    const { pet, care, conversation } = StateObserver.getState();

    const { id } = fromEntityId(this.view.entityId);
    const { action, statusEffects, species } = pet[id];
    const { activeScript } = conversation[id];

    const args = {
      id,
      action,
      species,
      ...statusEffects,
      ...pet[id].attributes,
      activeScript,
      currentCareAction: !!care.currentCareAction,
    };

    if (args.species) {
      this.view.setAnimation(getPetAnimation(args, 'walking'));
    }

    await super.animateViewPosition(sceneView, destination, duration);

    if (args.species) {
      this.view.setAnimation(getPetAnimation(args, 'idle'));
    }
  }
}
